import { supabase } from '../client';

import { RestaurantData, CustomerPreferences, BillingInfo, BookingStatus, Booking, MenuCategory, MenuItem } from '@deskbay/shared/types';

import { logger } from '@deskbay/shared';

import type { AvailableRestaurantRow, DbBookingRow, DbTableRow, DbRoomRow, DbCategoryRow, DbMenuItemRow } from './types';


/**
 * Recupera la lista di tutti i ristoranti accessibili all'utente (Owner + Membro).
 * Grazie alla RLS aggiornata, una semplice select ritorna tutto ciò che l'utente può vedere.
 */
export async function getAvailableRestaurants(userId: string): Promise<AvailableRestaurantRow[]> {
  const startTime = performance.now();

  // [PERFORMANCE] Execute owned and member queries in PARALLEL
  const [ownedResult, memberResult] = await Promise.all([
    // 1. Fetch Owned Restaurants
    supabase
      .from('restaurants')
      .select('id, name, slug, logo_url, owner_id, onboarding_status')
      .eq('owner_id', userId)
      .order('created_at', { ascending: true }),

    // 2. Fetch Member Restaurants
    // We use !inner to filter only restaurants where the user is a member
    supabase
      .from('restaurants')
      .select('id, name, slug, logo_url, owner_id, onboarding_status, restaurant_members!inner(user_id)')
      .eq('restaurant_members.user_id', userId)
      .order('created_at', { ascending: true })
  ]);

  // Handle errors
  if (ownedResult.error) {
    logger.api.error('Error fetching owned restaurants', ownedResult.error, 'database.getAvailableRestaurants.owned', { userId });
    return [];
  }

  if (memberResult.error) {
    logger.api.error('Error fetching member restaurants', memberResult.error, 'database.getAvailableRestaurants.member', { userId });
  }

  // 3. Merge and Deduplicate
  const owned = ownedResult.data || [];
  const members = memberResult.data || [];

  // Create a map by ID to deduplicate (though ideally a user shouldn't be both owner and member, but safe is better)
  const allRestaurantsMap = new Map();

  owned.forEach(r => allRestaurantsMap.set(r.id, r));
  members.forEach(r => allRestaurantsMap.set(r.id, r));

  const duration = performance.now() - startTime;
  logger.performance.info(`getAvailableRestaurants: ${duration.toFixed(0)}ms`, 'database.getAvailableRestaurants');

  return Array.from(allRestaurantsMap.values());
}


/**
 * Recupera i dati COMPLETI di un singolo ristorante specifico tramite ID.
 * Accessibile solo se l'utente ha i permessi (verificato da RLS).
 */
export async function getRestaurantDetail(restaurantId: string, effectiveUserId?: string, callerAuthUser?: { id: string; email?: string; user_metadata?: Record<string, string> }, adminBypass?: boolean): Promise<RestaurantData | null> {
  const startTime = performance.now();

  // 1. Fetch Dati Ristorante (explicit column list — no select('*'))
  const { data: restaurantRaw, error: resError } = await supabase
    .from('restaurants')
    .select([
      'id', 'slug', 'public_uuid', 'short_code', 'owner_id', 'name', 'subtitle', 'onboarding_status',
      'address', 'city', 'province', 'zip_code', 'email_public',
      'review_url', 'website_url', 'custom_button_label', 'custom_button_url', 'custom_button_label_translations', 'phone_contacts',
      'logo_url', 'cover_url', 'bio', 'bio_translations',
      'opening_hours', 'holiday_closures', 'social_links',
      'theme_config', 'gallery_urls', 'services',
      'billing_info', 'preferences',
      'allergens_list', 'enabled_allergen_ids',
      'booking_status', 'calendar_enabled', 'hide_booking_when_open',
      'reservation_duration_min', 'reservation_min_notice_min',
      'reservation_max_advance_days', 'reservation_cutoff_time',
      'reservation_public_opening_time', 'booking_mode',
      'cancellation_policy_hours', 'notification_language',
      'enable_cancellation', 'auto_confirm_bookings',
      'timezone', 'infinite_mode_max_seats',
      'booking_max_party_size', 'booking_accept_large_groups',
      'booking_slot_mode', 'booking_manual_slots',
      'notification_preferences',
      'seo_title', 'seo_description', 'seo_language', 'seo_indexable',
      'pdf_settings',
      'menu_currency',
      'additional_languages', 'default_language', 'source_language',
      'ai_assistant_enabled',
      'locale'
    ].join(','))
    .eq('id', restaurantId)
    .single();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase computed select string (array.join) produce GenericStringError; impossibile inferire tipi colonna a compile time
  const restaurant = restaurantRaw as any;

  if (resError) {
    logger.api.error('Error fetching restaurant detail', resError, 'database.getRestaurantDetail', { restaurantId });
  }

  if (!restaurant) {
    return null;
  }

  // Check status (whitelist approach as before)
  // Admin bypass skips this check so impersonation can see any onboarding state
  const incompleteStatuses = ['in_progress'];
  if (!adminBypass && restaurant.onboarding_status && incompleteStatuses.includes(restaurant.onboarding_status)) {
    return null;
  }

  // [PERFORMANCE] Get auth user ONCE before Promise.all
  // Priority order to avoid AuthSessionMissingError on Capacitor native:
  // 1. callerAuthUser: passed by AuthContext from the already-known session (best, no network)
  // 2. effectiveUserId: build a minimal user object from the known userId (no network)
  // 3. getSession(): local localStorage read, fallback for web-only callers
  let authUser = callerAuthUser;
  if (!authUser && effectiveUserId) {
    authUser = { id: effectiveUserId, email: '' };
  }
  if (!authUser) {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    authUser = session?.user ?? undefined;
    if (sessionError || !authUser) {
      logger.auth.error('Auth error in getRestaurantDetail', sessionError, 'database.getRestaurantDetail');
      return null;
    }
  }

  // [Impersonation Logic] Use effectiveUserId if provided (for Admin Impersonation)
  const currentUserId = effectiveUserId || authUser.id;
  const isOwner = restaurant.owner_id === currentUserId;

  // 2. Fetch Dati Correlati in PARALLEL (Performance Optimization)
  // [PERFORMANCE] All queries now truly parallel, no nested auth.getUser() calls
  const promiseAllStart = performance.now();

  const [
    bookingsResult,
    roomsResult,
    memberResult,
    profileResult,
    menuResult
  ] = await Promise.all([

    // Query 2: Bookings (with LIMIT and date filter for performance)
    (async () => {
      const start = performance.now();
      const result = await supabase
        .from('bookings')
        .select('*')
        .eq('restaurant_id', restaurant.id)
        .gte('booking_date', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
        .order('booking_date', { ascending: false })
        .limit(500);
      logger.performance.debug(`bookings: ${(performance.now() - start).toFixed(0)}ms`, 'database.getRestaurantDetail');
      return result;
    })(),

    // Query 3: Rooms + Tables (conditional loading based on booking mode)
    (async () => {
      if (restaurant.booking_mode !== 'table_based') {
        logger.performance.debug('rooms: 0ms (skipped)', 'database.getRestaurantDetail');
        return { data: [] };
      }
      const start = performance.now();
      const result = await supabase
        .from('restaurant_rooms')
        .select('*, tables:restaurant_tables(*)')
        .eq('restaurant_id', restaurant.id);
      logger.performance.debug(`rooms+tables: ${(performance.now() - start).toFixed(0)}ms`, 'database.getRestaurantDetail');
      return result;
    })(),

    // Query 4: Member permissions (skip if owner)
    (async () => {
      if (isOwner) {
        logger.performance.debug('member: 0ms (skipped)', 'database.getRestaurantDetail');
        return { data: null };
      }
      const start = performance.now();
      const result = await supabase
        .from('restaurant_members')
        .select('permissions, job_title')
        .eq('restaurant_id', restaurant.id)
        .eq('user_id', currentUserId)
        .maybeSingle();
      logger.performance.debug(`member: ${(performance.now() - start).toFixed(0)}ms`, 'database.getRestaurantDetail');
      return result;
    })(),

    // Query 5: Profile (always needed, moved from sequential to parallel)
    (async () => {
      const start = performance.now();
      const result = await supabase
        .from('profiles')
        .select('*')
        .eq('id', currentUserId)
        .single();
      logger.performance.debug(`profile: ${(performance.now() - start).toFixed(0)}ms`, 'database.getRestaurantDetail');
      return result;
    })(),

    // Query 6: Menu categories + items — in parallel with rest, eliminates sequential lazy load
    (async () => {
      const start = performance.now();
      const result = await supabase
        .from('categories')
        .select('*, items:menu_items(*)')
        .eq('restaurant_id', restaurant.id)
        .order('sort_order', { ascending: true });
      logger.performance.debug(`menu: ${(performance.now() - start).toFixed(0)}ms`, 'database.getRestaurantDetail');
      return result;
    })()
  ]);

  logger.performance.info(`Promise.all total: ${(performance.now() - promiseAllStart).toFixed(0)}ms`, 'database.getRestaurantDetail');

  // Extract data from results
  const bookings = bookingsResult.data;
  const roomsData = roomsResult.data;

  // 3. Determine Role and Permissions
  const permissions = isOwner ? {} : (memberResult.data?.permissions || {});
  const role = isOwner ? 'owner' : 'member';
  const profile = profileResult.data;

  // 5. Construct Response
  const defaultPrefs: CustomerPreferences = {
    marketingAIHtml: '',
    calendarEnabled: false,
    calendarOnlyWhenClosed: false,
    colorThemePreset: '0',
    customBgColor: '#FFFFFF',
    customTextColor: '#111827',
    customAccentColor: '#0ea5e9',
    pageLayoutPreset: '1',
    specialClosures: '[]',
    whatsappShowOnProfile: true,
    whatsappShowOnlyWhenClosed: false,
    marketingLastGenerationDate: '',
    emailShowOnProfile: true,
    promotedDishJson: '',
    notificationWhatsAppType: 'none',
    notificationWhatsAppCustomNumber: { prefix: '+39', number: '' },
    notificationEmailType: 'none',
    notificationEmailCustomAddress: '',
    fullyBookedDates: [],
    hideTotalInReceipt: true,
    hideMyOrder: false
  };

  // Map snake_case DB keys to camelCase for CustomerPreferences
  const rawPrefs = restaurant.preferences || {};
  const savedPrefs: Partial<CustomerPreferences> = {
    marketingAIHtml: rawPrefs.marketing_ai_html,
    colorThemePreset: rawPrefs.color_theme_preset,
    customBgColor: rawPrefs.custom_bg_color,
    customTitleColor: rawPrefs.custom_title_color,
    customCategoryColor: rawPrefs.custom_category_color,
    customTextColor: rawPrefs.custom_text_color,
    customDescriptionColor: rawPrefs.custom_description_color,
    customAccentColor: rawPrefs.custom_accent_color,
    pageLayoutPreset: rawPrefs.page_layout_preset,
    specialClosures: rawPrefs.special_closures,
    whatsappShowOnProfile: rawPrefs.whatsapp_show_on_profile,
    whatsappShowOnlyWhenClosed: rawPrefs.whatsapp_show_only_when_closed,
    marketingLastGenerationDate: rawPrefs.marketing_last_generation_date,
    emailShowOnProfile: rawPrefs.email_show_on_profile,
    promotedDishJson: rawPrefs.promoted_dish_json,
    notificationWhatsAppType: rawPrefs.notification_whatsapp_type,
    notificationWhatsAppCustomNumber: rawPrefs.notification_whatsapp_custom_number,
    notificationEmailType: rawPrefs.notification_email_type,
    notificationEmailCustomAddress: rawPrefs.notification_email_custom_address,
    fullyBookedDates: rawPrefs.fully_booked_dates,
    hideTotalInReceipt: rawPrefs.hide_total_in_receipt,
    hideMyOrder: rawPrefs.hide_my_order,
    priceZeroCheckEnabled: rawPrefs.price_zero_check_enabled,
    priceShowDecimals: rawPrefs.price_show_decimals,
    multilingualEnabled: rawPrefs.multilingual_enabled,
    bookingPhoneShowOnProfile: rawPrefs.booking_phone_show_on_profile,
    showMenuButtonOnProfile: rawPrefs.show_menu_button_on_profile,
    customButtonShowOnProfile: rawPrefs.custom_button_show_on_profile,
    // Multilingual support — read from dedicated columns (promoted from preferences)
    additionalLanguages: restaurant.additional_languages || [],
    defaultLanguage: restaurant.default_language || 'it',
    sourceLanguage: restaurant.source_language || 'it'
  };

  const duration = performance.now() - startTime;
  const breakdown = {
    total: duration.toFixed(0),
    restaurant_query: (promiseAllStart - startTime).toFixed(0),
    parallel_queries: (performance.now() - promiseAllStart).toFixed(0)
  };
  logger.performance.info(`getRestaurantDetail: ${breakdown.total}ms`, 'database.getRestaurantDetail', breakdown);
  if (duration > 1000) {
    logger.performance.warn(`SLOW getRestaurantDetail: ${duration.toFixed(0)}ms`, 'database.getRestaurantDetail');
  }

  return {
    directoryId: restaurant.id,
    slug: restaurant.slug,
    publicUuid: restaurant.public_uuid || undefined,
    shortCode: restaurant.short_code || undefined,
    ownerId: restaurant.owner_id,
    // User data is purely "Who is looking at this", not "Who owns this"
    user: {
      id: currentUserId,
      // Prefer profiles.email over auth email when auth email is Apple Private Relay.
      // profiles.email is set during onboarding when user provides their real email.
      // All external systems (FW360, stripe-webhook) already read from profiles.email.
      email: (profile?.email && !profile.email.endsWith('@privaterelay.appleid.com'))
        ? profile.email
        : (authUser.email || ''),
      firstName: profile?.first_name || authUser?.user_metadata?.firstName || '',
      lastName: profile?.last_name || authUser?.user_metadata?.lastName || '',
      phone: profile?.phone || '',
      lang: profile?.lang || authUser?.user_metadata?.lang || 'en',
    },
    // New field to help Frontend identify permissions
    currentUserRole: role as 'owner' | 'member',
    permissions: permissions,

    // SEO Data
    seo_title: restaurant.seo_title || '',
    seo_description: restaurant.seo_description || '',
    seo_language: restaurant.seo_language || 'it',
    seo_indexable: restaurant.seo_indexable ?? false,

    businessInfo: {
      name: restaurant.name,
      subtitle: restaurant.subtitle || '',
      address: restaurant.address || '',
      city: restaurant.city || '',
      province: restaurant.province || '',
      zipCode: restaurant.zip_code || '',
      phone: restaurant.phone_contacts?.primary || { prefix: '+39', number: '' },
      internalPhone: restaurant.phone_contacts?.internal || { prefix: '+39', number: '' },
      bookingPhone: restaurant.phone_contacts?.booking || { prefix: '+39', number: '' },
      whatsappPhone: restaurant.phone_contacts?.whatsapp || { prefix: '+39', number: '' },
      restaurantEmail: restaurant.email_public || '',
      showWhatsappButton: savedPrefs.whatsappShowOnProfile ?? true,
      reviewUrl: restaurant.review_url || '',
      websiteUrl: restaurant.website_url || '',
      country: restaurant.locale || '',
      customButtonLabel: restaurant.custom_button_label || '',
      customButtonUrl: restaurant.custom_button_url || '',
      customButtonLabelTranslations: restaurant.custom_button_label_translations || {}
    },
    profileInfo: {
      logoUrl: restaurant.logo_url || '',
      coverUrl: restaurant.cover_url || '',
      bio: restaurant.bio || '',
      bioTranslations: restaurant.bio_translations || {},
      openingHours: restaurant.opening_hours || [],
      holidayClosures: restaurant.holiday_closures || [],
      socialLinks: restaurant.social_links || [],
      theme: restaurant.theme_config?.theme || 'light',
      layout: restaurant.theme_config?.layout || 'classic',
      customThemeColors: restaurant.theme_config?.customColors,
      fontPreset: restaurant.theme_config?.fontPreset || 'default',
      customFonts: restaurant.theme_config?.customFonts || {},
      gallery: restaurant.gallery_urls || [],
      services: restaurant.services || []
    },
    billingInfo: {
      paese: restaurant.billing_info?.paese || 'ES',
      ragione_sociale: restaurant.billing_info?.ragione_sociale || '',
      piva: restaurant.billing_info?.piva || '',
      cf: restaurant.billing_info?.cf || '',
      pec: restaurant.billing_info?.pec || '',
      sdi: restaurant.billing_info?.sdi || '',
      indirizzo: restaurant.billing_info?.indirizzo || '',
      stato: restaurant.billing_info?.stato || 'España',
      citta: restaurant.billing_info?.citta || '',
      provincia: restaurant.billing_info?.provincia || '',
      comune: restaurant.billing_info?.comune || '',
      cap: restaurant.billing_info?.cap || '',
    },
    menu: (menuResult.data || []).map((cat: DbCategoryRow) => ({
      id: cat.id,
      categoryName: cat.name,
      description: cat.description || '',
      imageUrl: cat.image_url || '',
      translations: (cat.translations || {}) as MenuCategory['translations'],
      items: (cat.items || []).sort((a: DbMenuItemRow, b: DbMenuItemRow) => (a.sort_order || 0) - (b.sort_order || 0)).map((item: DbMenuItemRow) => ({
        id: item.id,
        name: item.name,
        description: item.description || '',
        price: item.price || '',
        priceCents: item.price_cents ?? null,
        variants: (item.variants || undefined) as MenuItem['variants'],
        imageUrl: item.image_url || '',
        isActive: item.is_active ?? true,
        temporary_reopen_at: item.temporary_reopen_at || null,
        hideBasePrice: item.hide_base_price ?? false,
        basePriceLabel: item.base_price_label || undefined,
        allergenIds: item.allergen_ids || [],
        tagIds: item.tag_ids || [],
        translations: (item.translations || {}) as MenuItem['translations']
      }))
    })),
    allergens: restaurant.allergens_list || [],
    bookings: (bookings || []).map((b: DbBookingRow) => ({
      id: b.id,
      restaurantId: b.restaurant_id,
      booking_number: b.booking_number,
      cancelled_by: b.cancelled_by as Booking['cancelled_by'],
      booking_date: b.booking_date,
      booking_time: b.booking_time,
      partySize: b.party_size,
      name: b.customer_name,
      phone: b.customer_phone,
      email: b.customer_email,
      notes: b.notes,
      createdAt: b.created_at,
      tableId: b.table_id,
      status: b.status as Booking['status'],
      lang: b.lang,
      isArrived: b.is_arrived ?? false,
      source: b.source as Booking['source'],
    })),
    rooms: (roomsData || []).map((r: DbRoomRow) => ({
      id: r.id,
      name: r.name,
      tables: (r.tables || [])
        .sort((a: DbTableRow, b: DbTableRow) => (a.sort_order || 0) - (b.sort_order || 0))
        .map((t: DbTableRow) => ({
          id: t.id,
          name: t.name,
          capacity: t.seats,
          roomId: r.id,
          sort_order: t.sort_order || 0
        }))
    })),
    bookingSettings: {
      bookingStatus: (restaurant.booking_status as BookingStatus) || 'disabled',
      enabled: restaurant.booking_status === 'enabled',
      hideBookingWhenOpen: restaurant.hide_booking_when_open ?? false,
      duration: restaurant.reservation_duration_min || 90,
      minNotice: restaurant.reservation_min_notice_min || 60,
      maxAdvance: restaurant.reservation_max_advance_days || 30,
      cutoffTime: restaurant.reservation_cutoff_time || '22:00',
      publicOpeningTime: restaurant.reservation_public_opening_time || '19:00',
      bookingMode: (restaurant.booking_mode as 'infinite' | 'table_based') || 'infinite',
      cancellationPolicyHours: restaurant.cancellation_policy_hours || 24,
      notification_language: restaurant.notification_language || 'it',
      enable_cancellation: restaurant.enable_cancellation ?? true,
      autoConfirm: restaurant.auto_confirm_bookings !== false,
      timezone: restaurant.timezone || 'Europe/Rome',
      infiniteModeMaxSeats: restaurant.infinite_mode_max_seats || 20,
      maxPartySize: restaurant.booking_max_party_size ?? null,
      acceptLargeGroups: restaurant.booking_accept_large_groups ?? false,
      slotMode: (restaurant.booking_slot_mode as 'auto' | 'manual') || 'manual',
      manualSlots: restaurant.booking_manual_slots || {},
      notification_preferences: restaurant.notification_preferences
    },
    enabledAllergenIds: restaurant.enabled_allergen_ids || [],
    customerPreferences: {
      ...defaultPrefs,
      // FIX: strip undefined values so they don't override defaults (e.g. hideTotalInReceipt: false)
      // When a new account has no preferences JSON, all rawPrefs fields read as undefined.
      // Spreading undefined over defaultPrefs would wipe out sensible defaults.
      ...Object.fromEntries(Object.entries(savedPrefs).filter(([, v]) => v !== undefined)) as Partial<CustomerPreferences>,
      // CRITICAL: Deriva calendarEnabled da booking_status (source of truth)
      // disabled/reset → false; enabled/paused → true
      // Il wizard usa bookingStatus direttamente, non calendarEnabled
      calendarEnabled: restaurant.booking_status === 'enabled' || restaurant.booking_status === 'paused'
    },
    onboarding_status: restaurant.onboarding_status as 'completed' | 'in_progress' | 'finalizing' | undefined,
    pdfSettings: restaurant.pdf_settings || undefined,
    menuCurrency: restaurant.menu_currency ?? 'EUR',
    ai_assistant_enabled: restaurant.ai_assistant_enabled ?? false
  };
}


/**
 * Salva tutte le impostazioni del ristorante.
 */
export async function updateRestaurant(id: string | number, data: RestaurantData) {
  // FIX: Costruisci notification_preferences basandosi sulle scelte UI
  // L'utente sceglie in customerPreferences.notificationWhatsAppType e .notificationWhatsAppCustomNumber
  // Ma bookingService.ts legge da notification_preferences.custom_phone
  const prefs = (data.customerPreferences || {}) as CustomerPreferences;
  let customPhoneForNotifications: string | null = null;
  let customEmailForNotifications: string | null = null;

  // Determina il numero di telefono per le notifiche WhatsApp
  if (prefs.notificationWhatsAppType === 'custom' && prefs.notificationWhatsAppCustomNumber) {
    const p = prefs.notificationWhatsAppCustomNumber;
    customPhoneForNotifications = p.prefix && p.number ? `${p.prefix}${p.number}` : null;
  } else if (prefs.notificationWhatsAppType === 'locale') {
    const p = data.businessInfo.whatsappPhone;
    customPhoneForNotifications = p?.prefix && p?.number ? `${p.prefix}${p.number}` : null;
  } else if (prefs.notificationWhatsAppType === 'owner') {
    // Owner phone viene da profiles, lo leggiamo al volo se serve, oppure usiamo user.phone se disponibile
    customPhoneForNotifications = data.user?.phone || null;
  }
  // Se 'none', rimane null (niente WhatsApp)

  // Determina l'email per le notifiche Email
  if (prefs.notificationEmailType === 'custom') {
    customEmailForNotifications = prefs.notificationEmailCustomAddress || null;
  } else if (prefs.notificationEmailType === 'public') {
    customEmailForNotifications = data.businessInfo.restaurantEmail || null;
  } else if (prefs.notificationEmailType === 'owner') {
    customEmailForNotifications = data.user?.email || null;
  }
  // Se 'none', rimane null (niente Email)

  // Costruisci notification_preferences sincronizzato (con .trim() per sicurezza)
  const notificationPreferences = {
    whatsapp: prefs.notificationWhatsAppType !== 'none',
    email: prefs.notificationEmailType !== 'none',
    custom_phone: customPhoneForNotifications?.trim() || null,
    custom_email: customEmailForNotifications?.trim() || null
  };

  // Helper per pulire orari (rimuove secondi se presenti)
  const cleanTime = (t: string | undefined | null) => {
    if (!t) return null;
    if (t.length > 5) return t.substring(0, 5);
    return t;
  };

  // Helper per convertire stringhe vuote in null (per evitare trigger mismatch su campi nullable)
  const cleanNullableString = (s: string | undefined | null) => {
    if (!s || s.trim() === '') return null;
    return s;
  };

  // Pulisci orari negli opening_hours
  const cleanedOpeningHours = (data.profileInfo.openingHours || []).map(day => ({
    ...day,
    slots: day.slots.map(slot => ({
      from: cleanTime(slot.from) || '00:00',
      to: cleanTime(slot.to) || '00:00'
    }))
  }));

  // Costruisci l'oggetto di aggiornamento
  const updatePayload: Record<string, unknown> = {
    name: data.businessInfo.name,
    subtitle: cleanNullableString(data.businessInfo.subtitle),
    address: data.businessInfo.address,
    city: data.businessInfo.city,
    province: data.businessInfo.province,
    zip_code: data.businessInfo.zipCode,
    locale: cleanNullableString(data.businessInfo.country),
    email_public: cleanNullableString(data.businessInfo.restaurantEmail),
    review_url: cleanNullableString(data.businessInfo.reviewUrl),
    website_url: cleanNullableString(data.businessInfo.websiteUrl),
    custom_button_label: cleanNullableString(data.businessInfo.customButtonLabel),
    custom_button_url: cleanNullableString(data.businessInfo.customButtonUrl),
    custom_button_label_translations: data.businessInfo.customButtonLabelTranslations || {},
    bio: cleanNullableString(data.profileInfo.bio),
    bio_translations: data.profileInfo.bioTranslations || {},
    logo_url: cleanNullableString(data.profileInfo.logoUrl),
    cover_url: cleanNullableString(data.profileInfo.coverUrl),
    social_links: data.profileInfo.socialLinks,
    opening_hours: cleanedOpeningHours,
    holiday_closures: data.profileInfo.holidayClosures,
    theme_config: {
      theme: data.profileInfo.theme,
      layout: data.profileInfo.layout,
      customColors: data.profileInfo.customThemeColors,
      fontPreset: data.profileInfo.fontPreset,
      customFonts: data.profileInfo.customFonts
    },
    gallery_urls: data.profileInfo.gallery,
    services: data.profileInfo.services || [],
    // FIX: preserve primary phone alongside derived phone channels
    phone_contacts: {
      primary: data.businessInfo.phone,
      internal: data.businessInfo.internalPhone,
      booking: data.businessInfo.bookingPhone,
      whatsapp: data.businessInfo.whatsappPhone
    },
    billing_info: data.billingInfo,
    booking_status: data.bookingSettings.bookingStatus ?? (data.bookingSettings.enabled ? 'enabled' : 'disabled'),
    calendar_enabled: (data.bookingSettings.bookingStatus ?? (data.bookingSettings.enabled ? 'enabled' : 'disabled')) === 'enabled',
    hide_booking_when_open: data.bookingSettings.hideBookingWhenOpen,
    reservation_duration_min: data.bookingSettings.duration,
    reservation_min_notice_min: data.bookingSettings.minNotice,
    reservation_max_advance_days: data.bookingSettings.maxAdvance,
    reservation_cutoff_time: cleanTime(data.bookingSettings.cutoffTime),
    reservation_public_opening_time: cleanTime(data.bookingSettings.publicOpeningTime),
    booking_mode: data.bookingSettings.bookingMode,
    booking_slot_mode: data.bookingSettings.slotMode || 'manual',
    booking_manual_slots: data.bookingSettings.manualSlots || {},
    cancellation_policy_hours: data.bookingSettings.cancellationPolicyHours,
    enable_cancellation: data.bookingSettings.enable_cancellation,
    auto_confirm_bookings: data.bookingSettings.autoConfirm,
    timezone: data.bookingSettings.timezone,
    notification_language: data.bookingSettings.notification_language,
    infinite_mode_max_seats: data.bookingSettings.infiniteModeMaxSeats,
    booking_max_party_size: data.bookingSettings.maxPartySize ?? null,
    booking_accept_large_groups: data.bookingSettings.acceptLargeGroups ?? false,
    notification_preferences: notificationPreferences,
    // Multilingual support — dedicated columns (promoted from preferences)
    additional_languages: prefs.additionalLanguages || [],
    default_language: prefs.defaultLanguage || 'it',
    source_language: prefs.sourceLanguage || 'it',
    preferences: {
      marketing_ai_html: prefs.marketingAIHtml,
      color_theme_preset: prefs.colorThemePreset,
      custom_bg_color: prefs.customBgColor,
      custom_title_color: prefs.customTitleColor,
      custom_category_color: prefs.customCategoryColor,
      custom_text_color: prefs.customTextColor,
      custom_description_color: prefs.customDescriptionColor,
      custom_accent_color: prefs.customAccentColor,
      page_layout_preset: prefs.pageLayoutPreset,
      special_closures: prefs.specialClosures,
      whatsapp_show_on_profile: prefs.whatsappShowOnProfile,
      whatsapp_show_only_when_closed: prefs.whatsappShowOnlyWhenClosed,
      marketing_last_generation_date: prefs.marketingLastGenerationDate,
      email_show_on_profile: prefs.emailShowOnProfile,
      promoted_dish_json: prefs.promotedDishJson,
      notification_whatsapp_type: prefs.notificationWhatsAppType,
      notification_whatsapp_custom_number: prefs.notificationWhatsAppCustomNumber,
      notification_email_type: prefs.notificationEmailType,
      notification_email_custom_address: prefs.notificationEmailCustomAddress,
      fully_booked_dates: prefs.fullyBookedDates,
      hide_total_in_receipt: prefs.hideTotalInReceipt,
      hide_my_order: prefs.hideMyOrder,
      price_zero_check_enabled: prefs.priceZeroCheckEnabled,
      price_show_decimals: prefs.priceShowDecimals,
      multilingual_enabled: prefs.multilingualEnabled,
      booking_phone_show_on_profile: prefs.bookingPhoneShowOnProfile,
      show_menu_button_on_profile: prefs.showMenuButtonOnProfile,
      custom_button_show_on_profile: prefs.customButtonShowOnProfile,
      // DUAL WRITE: keep in preferences for backward compat with get_restaurant_languages() SQL function
      additional_languages: prefs.additionalLanguages || [],
      default_language: prefs.defaultLanguage || 'it'
    },
    allergens_list: data.allergens,
    enabled_allergen_ids: data.enabledAllergenIds || []
  };

  // Aggiungi pdf_settings solo se definito (evita crash se colonna manca)
  if (data.pdfSettings) {
    updatePayload.pdf_settings = data.pdfSettings;
  }
  // Salva menu_currency — colonna diretta su restaurants
  if (data.menuCurrency) {
    updatePayload.menu_currency = data.menuCurrency;
  }

  const { error } = await supabase
    .from('restaurants')
    .update(updatePayload)
    .eq('id', id);

  if (error) throw error;
}


/**
 * Aggiorna SOLO i dati di fatturazione del ristorante.
 * Usato quando il ristorante non ha ancora una subscription attiva
 * (es. multi-restaurant pre-checkout) per evitare che il trigger
 * check_subscription_for_update blocchi l'update completo.
 */
export async function updateBillingOnly(id: string | number, billingInfo: BillingInfo) {
  const { error } = await supabase
    .from('restaurants')
    .update({ billing_info: billingInfo })
    .eq('id', id);

  if (error) throw error;
}


/**
 * Recupera i dati pubblici del ristorante tramite SLUG.
 */
export async function getRestaurantBySlug(slug: string): Promise<RestaurantData | null> {
  const { data: restaurantSlugRaw, error: resError } = await supabase
    .from('restaurants')
    .select([
      'id', 'slug', 'public_uuid', 'short_code', 'owner_id', 'name', 'subtitle',
      'address', 'city', 'province', 'zip_code', 'email_public',
      'review_url', 'website_url', 'custom_button_label', 'custom_button_url', 'custom_button_label_translations', 'phone_contacts',
      'logo_url', 'cover_url', 'bio', 'bio_translations',
      'opening_hours', 'holiday_closures', 'social_links',
      'theme_config', 'gallery_urls', 'services',
      'preferences', 'allergens_list', 'enabled_allergen_ids',
      'booking_status', 'calendar_enabled', 'hide_booking_when_open',
      'reservation_duration_min', 'reservation_min_notice_min',
      'reservation_max_advance_days', 'reservation_cutoff_time',
      'reservation_public_opening_time',
      'booking_mode', 'cancellation_policy_hours',
      'enable_cancellation', 'auto_confirm_bookings', 'timezone',
      'infinite_mode_max_seats',
      'booking_max_party_size', 'booking_accept_large_groups',
      'notification_language', 'booking_slot_mode', 'booking_manual_slots',
      'seo_title', 'seo_description', 'seo_language', 'seo_indexable',
      'menu_currency',
      'additional_languages', 'default_language', 'source_language',
      'locale'
    ].join(','))
    .eq('slug', slug)
    .maybeSingle();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase computed select string (array.join) produce GenericStringError; impossibile inferire tipi colonna a compile time
  const restaurant = restaurantSlugRaw as any;
  if (resError || !restaurant) return null;

  const [{ data: categories }, { data: roomsData }] = await Promise.all([
    supabase
      .from('categories')
      .select('*, items:menu_items(*)')
      .eq('restaurant_id', restaurant.id)
      .order('sort_order', { ascending: true }),
    supabase
      .from('restaurant_rooms')
      .select('*, tables:restaurant_tables(*)')
      .eq('restaurant_id', restaurant.id)
  ]);

  // Map snake_case DB keys to camelCase for CustomerPreferences
  const rawPrefs = restaurant.preferences || {};
  const savedPrefs: Partial<CustomerPreferences> = {
    marketingAIHtml: rawPrefs.marketing_ai_html,
    colorThemePreset: rawPrefs.color_theme_preset,
    customBgColor: rawPrefs.custom_bg_color,
    customTitleColor: rawPrefs.custom_title_color,
    customCategoryColor: rawPrefs.custom_category_color,
    customTextColor: rawPrefs.custom_text_color,
    customDescriptionColor: rawPrefs.custom_description_color,
    customAccentColor: rawPrefs.custom_accent_color,
    pageLayoutPreset: rawPrefs.page_layout_preset,
    specialClosures: rawPrefs.special_closures,
    whatsappShowOnProfile: rawPrefs.whatsapp_show_on_profile,
    whatsappShowOnlyWhenClosed: rawPrefs.whatsapp_show_only_when_closed,
    marketingLastGenerationDate: rawPrefs.marketing_last_generation_date,
    emailShowOnProfile: rawPrefs.email_show_on_profile,
    promotedDishJson: rawPrefs.promoted_dish_json,
    notificationWhatsAppType: rawPrefs.notification_whatsapp_type,
    notificationWhatsAppCustomNumber: rawPrefs.notification_whatsapp_custom_number,
    notificationEmailType: rawPrefs.notification_email_type,
    notificationEmailCustomAddress: rawPrefs.notification_email_custom_address,
    fullyBookedDates: rawPrefs.fully_booked_dates,
    hideTotalInReceipt: rawPrefs.hide_total_in_receipt,
    hideMyOrder: rawPrefs.hide_my_order,
    priceZeroCheckEnabled: rawPrefs.price_zero_check_enabled,
    priceShowDecimals: rawPrefs.price_show_decimals,
    multilingualEnabled: rawPrefs.multilingual_enabled,
    bookingPhoneShowOnProfile: rawPrefs.booking_phone_show_on_profile,
    showMenuButtonOnProfile: rawPrefs.show_menu_button_on_profile,
    customButtonShowOnProfile: rawPrefs.custom_button_show_on_profile,
    // Multilingual support — read from dedicated columns (promoted from preferences)
    additionalLanguages: restaurant.additional_languages || [],
    defaultLanguage: restaurant.default_language || 'it',
    sourceLanguage: restaurant.source_language || 'it'
  };

  return {
    directoryId: restaurant.id,
    slug: restaurant.slug,
    publicUuid: restaurant.public_uuid || undefined,
    shortCode: restaurant.short_code || undefined,
    ownerId: restaurant.owner_id,
    user: { id: '', email: '', firstName: '', lastName: '', lang: 'it' }, // Public view = no user data

    // SEO Data
    seo_title: restaurant.seo_title || '',
    seo_description: restaurant.seo_description || '',
    seo_language: restaurant.seo_language || 'it',
    seo_indexable: restaurant.seo_indexable ?? false,

    businessInfo: {
      name: restaurant.name,
      subtitle: restaurant.subtitle || '',
      address: restaurant.address || '',
      city: restaurant.city || '',
      province: restaurant.province || '',
      zipCode: restaurant.zip_code || '',
      phone: restaurant.phone_contacts?.primary || { prefix: '+39', number: '' },
      internalPhone: restaurant.phone_contacts?.internal || { prefix: '+39', number: '' },
      bookingPhone: restaurant.phone_contacts?.booking || { prefix: '+39', number: '' },
      whatsappPhone: restaurant.phone_contacts?.whatsapp || { prefix: '+39', number: '' },
      restaurantEmail: restaurant.email_public || '',
      showWhatsappButton: savedPrefs.whatsappShowOnProfile ?? true,
      reviewUrl: restaurant.review_url || '',
      websiteUrl: restaurant.website_url || '',
      country: restaurant.locale || '',
      customButtonLabel: restaurant.custom_button_label || '',
      customButtonUrl: restaurant.custom_button_url || '',
      customButtonLabelTranslations: restaurant.custom_button_label_translations || {}
    },
    profileInfo: {
      logoUrl: restaurant.logo_url || '',
      coverUrl: restaurant.cover_url || '',
      bio: restaurant.bio || '',
      bioTranslations: restaurant.bio_translations || {},
      openingHours: restaurant.opening_hours || [],
      holidayClosures: restaurant.holiday_closures || [],
      socialLinks: restaurant.social_links || [],
      theme: restaurant.theme_config?.theme || 'light',
      layout: restaurant.theme_config?.layout || 'classic',
      customThemeColors: restaurant.theme_config?.customColors,
      fontPreset: restaurant.theme_config?.fontPreset || 'default',
      customFonts: restaurant.theme_config?.customFonts || {},
      gallery: restaurant.gallery_urls || [],
      services: restaurant.services || []
    },
    billingInfo: { // Billing info not needed for public profile, returning empty
      paese: 'ES', ragione_sociale: '', piva: '', cf: '', pec: '', sdi: '',
      indirizzo: '', stato: '', citta: '', comune: '', cap: '', provincia: ''
    },
    menu: (categories || []).map((cat: DbCategoryRow) => ({
      id: cat.id,
      categoryName: cat.name,
      description: cat.description || '',
      imageUrl: cat.image_url || '',
      translations: (cat.translations || {}) as MenuCategory['translations'],
      items: (cat.items || []).sort((a: DbMenuItemRow, b: DbMenuItemRow) => (a.sort_order || 0) - (b.sort_order || 0)).map((item: DbMenuItemRow) => ({
        id: item.id,
        name: item.name,
        description: item.description || '',
        price: item.price || '',
        priceCents: item.price_cents ?? null,
        variants: (item.variants || undefined) as MenuItem['variants'],
        imageUrl: item.image_url || '',
        isActive: item.is_active ?? true,
        temporary_reopen_at: item.temporary_reopen_at || null,
        hideBasePrice: item.hide_base_price ?? false,
        basePriceLabel: item.base_price_label || undefined,
        allergenIds: item.allergen_ids || [],
        tagIds: item.tag_ids || [],
        translations: (item.translations || {}) as MenuItem['translations']
      }))
    })),
    allergens: restaurant.allergens_list || [],
    bookings: [],
    rooms: (roomsData || []).map((r: DbRoomRow) => ({
      id: r.id,
      name: r.name,
      tables: (r.tables || [])
        .sort((a: DbTableRow, b: DbTableRow) => (a.sort_order || 0) - (b.sort_order || 0))
        .map((t: DbTableRow) => ({
          id: t.id,
          name: t.name,
          capacity: t.seats,
          roomId: r.id,
          sort_order: t.sort_order || 0
        }))
    })),
    bookingSettings: {
      bookingStatus: (restaurant.booking_status as BookingStatus) || 'disabled',
      enabled: restaurant.booking_status === 'enabled',
      hideBookingWhenOpen: restaurant.hide_booking_when_open ?? false,
      duration: restaurant.reservation_duration_min || 90,
      minNotice: restaurant.reservation_min_notice_min || 60,
      maxAdvance: restaurant.reservation_max_advance_days || 30,
      cutoffTime: restaurant.reservation_cutoff_time || '22:00',
      publicOpeningTime: restaurant.reservation_public_opening_time || '19:00',
      bookingMode: (restaurant.booking_mode as 'infinite' | 'table_based') || 'infinite',
      cancellationPolicyHours: restaurant.cancellation_policy_hours,
      enable_cancellation: restaurant.enable_cancellation ?? true,
      autoConfirm: restaurant.auto_confirm_bookings !== false,
      timezone: restaurant.timezone,
      infiniteModeMaxSeats: restaurant.infinite_mode_max_seats || 20,
      maxPartySize: restaurant.booking_max_party_size ?? null,
      acceptLargeGroups: restaurant.booking_accept_large_groups ?? false,
      notification_language: restaurant.notification_language,
      slotMode: (restaurant.booking_slot_mode as 'auto' | 'manual') || 'manual',
      manualSlots: restaurant.booking_manual_slots || {}
    },
    enabledAllergenIds: restaurant.enabled_allergen_ids || [],
    customerPreferences: {
      // FIX: strip undefined values so they don't override defaults
      ...Object.fromEntries(Object.entries(savedPrefs).filter(([, v]) => v !== undefined)) as Partial<CustomerPreferences>,
      // FIX: Sync calendarEnabled from booking_status (source of truth)
      calendarEnabled: restaurant.booking_status === 'enabled' || restaurant.booking_status === 'paused'
    },
    menuCurrency: restaurant.menu_currency ?? 'EUR'
  };
}
